import React, { useEffect } from "react"
import * as Sentry from "@sentry/browser"
import { Group, Text, ScrollArea } from "@mantine/core"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useMutation } from "@tanstack/react-query"

import { Button, ErrorIcon, Modal, Radio, Stack, Icon } from "@/components"
import { FocusTrap } from "@/components/FocusTrap"

import { useMantineTheme } from "@/hooks"
import { getUserPersonaOptions } from "shared/imports/sharedHelperFunctions"

import { useStyles } from "@/components/PersonaIdentificationModal/PersonaIdentificationModal.styles"

type FormValues = {
    persona: string
}

/**
 * The Persona Identification Modal (PIM), prompts the user to select a UserPersona if the user does not already have one.
 * It only renders after the user has accepted the Terms and Conditions, and is not a Quorum Employee logged in as a client.
 */
export const PersonaIdentificationModal = () => {
    const shouldRender = !Userdata.user_persona && Userdata.activated && !Userdata.logged_in_as_user
    const [opened, { open, close }] = useDisclosure(shouldRender)
    useEffect(() => {
        if (shouldRender) {
            open()
        }
    }, [open, shouldRender])

    const { classes } = useStyles()
    const theme = useMantineTheme()
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`)

    const nonGenericPersonas = getUserPersonaOptions().filter(
        (persona) => persona.value !== DjangIO.app.userdata.types.UserPersona.generic.value,
    )
    const reactHookForm = useForm<FormValues>({
        defaultValues: {
            persona: "",
        },
    })

    const {
        // Read the formState before render to subscribe the form state through the Proxy
        formState: { isSubmitting, isValid },
    } = reactHookForm

    const mutatePersona = useMutation({
        mutationFn: (formValues: FormValues) => {
            return DjangIO.app.userdata.models.QuorumUser.objects
                .use_action("update_user_information")
                .update({
                    userId: Userdata.id,
                    updateFields: {
                        user_persona: formValues.persona,
                    },
                })
                .PATCH()
        },
    })

    const onSubmit: SubmitHandler<FormValues> = async (formValues) => {
        await mutatePersona.mutateAsync(formValues, {
            onSuccess: () => {
                window.location.reload()
            },
            onError: (err) => {
                Sentry.captureException(err)

                notifications.show({
                    id: "persona-failure",
                    title: "Unable to save",
                    message: "Your selection could not be saved",
                    autoClose: false,
                    color: "red.0",
                    icon: <ErrorIcon />,
                    closeButtonProps: { "aria-label": "Dismiss notification" },
                    styles: (theme) => ({
                        description: {
                            color: theme.colors.gray[7],
                        },
                    }),
                })
                close()
            },
        })
    }

    return (
        <Modal.Root
            closeOnClickOutside={false}
            closeOnEscape={false}
            fullScreen={isMobile}
            onClose={close}
            opened={opened}
            size="auto"
            centered
            trapFocus={false} // Use a custom focus trap to address an a11y bug in Mantine
        >
            <Modal.Overlay />
            <Modal.Content className={classes.content}>
                <FormProvider {...reactHookForm}>
                    <FocusTrap active={opened}>
                        <form onSubmit={reactHookForm.handleSubmit(onSubmit)} className={classes.form}>
                            <Group className={classes.wrapper}>
                                <Modal.Header className={classes.header}>
                                    {isMobile && (
                                        <Icon
                                            color={theme.colors.brand[0]}
                                            icon="quorum-logo"
                                            iconFamily="q"
                                            size="3x"
                                        />
                                    )}
                                    <Modal.Title className={classes.title}>Enhance Your Quorum Experience</Modal.Title>
                                </Modal.Header>

                                <Modal.Body className={classes.body}>
                                    <Stack className={classes.section}>
                                        <Text className={classes.description}>
                                            Please select an option that best describes your role and motivations to
                                            receive a customized experience. You can update your choice anytime under
                                            Settings {">"} My Information {">"} Product Settings.
                                        </Text>

                                        <ScrollArea>
                                            <Radio.Group
                                                name="persona"
                                                className={classes.radioGroup}
                                                rules={{ required: true }}
                                            >
                                                <Stack spacing="xs" className={classes.options}>
                                                    {nonGenericPersonas.map((persona) => (
                                                        <Radio
                                                            value={String(persona.value)}
                                                            label={
                                                                <Group noWrap>
                                                                    <Icon
                                                                        icon={persona.icon}
                                                                        color={theme.colors.violet[7]}
                                                                    />
                                                                    <span
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: persona.summary,
                                                                        }}
                                                                    />
                                                                </Group>
                                                            }
                                                            key={persona.value}
                                                            className={classes.option}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Radio.Group>
                                        </ScrollArea>
                                    </Stack>
                                </Modal.Body>
                            </Group>

                            <Group className={classes.actions}>
                                <Button
                                    type="submit"
                                    variant="filled"
                                    loading={isSubmitting}
                                    {...(!isValid && {
                                        "data-disabled": true,
                                        "aria-disabled": true,
                                    })}
                                >
                                    Save
                                </Button>
                            </Group>
                        </form>
                    </FocusTrap>
                </FormProvider>
            </Modal.Content>
        </Modal.Root>
    )
}
